import React, {useCallback, useEffect, useRef, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Typography
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import {Title, useDataProvider, useNotify} from "react-admin";
import {useClosedSidebar} from "../../shared/hooks/useClosedSdebar";
import {clientNoJson, endpoint} from "../../dataProvider";
import {ConvertNoteDraftFiles, ConvertNoteFilesWorkspace} from "./NoteConvertForm";

const priority: { [status: string]: number } = {CO: 0, IP: 1, CR: 2, ER: 3};

export function NoteConvertPage() {
    useClosedSidebar();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [notes, setNotes] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [selectedId, setSelectedId] = useState<any>(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [draftFiles, setDraftFiles] = useState<File[]>([]);
    const [autoStart, setAutoStart] = useState(true);
    const [uploading, setUploading] = useState(false);
    const uploadNoteId = useRef<any>(null);
    const uploaded = useRef(false);
    const fetching = useRef(false);
    const mounted = useRef(true);
    const previousIds = useRef<any[]>([]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const refetch = useCallback(async () => {
        if (fetching.current) return;
        fetching.current = true;
        try {
            const records: any[] = [];
            let page = 1;
            let total = Infinity;
            while (records.length < total) {
                const result = await dataProvider.getList("note_convert", {
                    pagination: {page, perPage: 100}, sort: {field: "id", order: "DESC"},
                    filter: {exclude_completed: true},
                });
                records.push(...result.data);
                total = result.total;
                if (!result.data.length) break;
                page += 1;
            }
            if (mounted.current) {
                setNotes(records);
                setLoadError(false);
            }
        } catch {
            if (mounted.current) {
                setLoadError(true);
                notify("Impossibile aggiornare le bolle", "warning");
            }
        } finally {
            fetching.current = false;
            if (mounted.current) setLoading(false);
        }
    }, [dataProvider, notify]);
    useEffect(() => {
        refetch();
    }, [refetch]);
    const processing = notes.some(note => ["Q", "IP"].includes(note.status));
    useEffect(() => {
        if (!processing) return;
        const timer = window.setInterval(refetch, 5000);
        return () => window.clearInterval(timer);
    }, [processing, refetch]);

    const files = notes.flatMap(note => (note.files || [])
        .filter((file: any) => file.status !== "CV").map((file: any) => ({note, file})))
        .sort((a, b) => (priority[a.file.status] ?? 9) - (priority[b.file.status] ?? 9));
    const selectedIndex = files.findIndex(item => String(item.file.id) === String(selectedId));
    // After conversion/removal continue with the next document, preserving selection on polling.
    const nextId = previousIds.current.slice(previousIds.current.indexOf(selectedId) + 1)
        .find(id => files.some(item => item.file.id === id));
    const fallbackIndex = files.findIndex(item => item.file.id === nextId);
    const index = selectedIndex < 0 ? Math.max(0, fallbackIndex) : selectedIndex;
    const current = files[index];
    const fileIds = files.map(item => item.file.id).join(",");
    useEffect(() => {
        if (selectedIndex < 0) setSelectedId(current?.file.id ?? null);
        previousIds.current = files.map(item => item.file.id);
    }, [selectedIndex, current?.file.id, fileIds]);

    const addFiles = (newFiles: FileList | File[]) => {
        const selected = Array.from(newFiles);
        const invalid = selected.find(file =>
            !["pdf", "jpg", "jpeg", "png", "tif", "tiff", "webp"].includes(file.name.split(".").pop()?.toLowerCase() || "") || file.size > 20 * 1024 * 1024);
        if (invalid) {
            notify(`“${invalid.name}” non è supportato oppure supera 20 MB`, "warning");
            return;
        }
        setDraftFiles(previous => [...previous, ...selected]);
    };
    const closeUpload = () => {
        if (uploading) return;
        setUploadOpen(false);
        setDraftFiles([]);
        uploadNoteId.current = null;
        uploaded.current = false;
    };
    const upload = async () => {
        if (!draftFiles.length || uploading) return;
        setUploading(true);
        try {
            // Reuse the same lot if the user retries a failed upload or start.
            if (!uploadNoteId.current) {
                const result = await dataProvider.create("note_convert", {data: {}});
                uploadNoteId.current = result.data.id;
            }
            if (!uploaded.current) {
                const data = new FormData();
                draftFiles.forEach(file => data.append("files", file));
                await clientNoJson(`${endpoint}/note_convert/${uploadNoteId.current}/upload_files/`, {
                    method: "POST", data, headers: {"Content-Type": "multipart/form-data"},
                });
                uploaded.current = true;
            }
            if (autoStart) await clientNoJson(`${endpoint}/note_convert/${uploadNoteId.current}/start/`, {method: "POST"});
            notify(autoStart ? "Bolle caricate: elaborazione avviata o in coda" : "Bolle caricate", "info");
            setUploadOpen(false);
            setDraftFiles([]);
            uploadNoteId.current = null;
            uploaded.current = false;
        } catch (error: any) {
            notify(error?.response?.data?.detail || "Operazione non riuscita. Puoi riprovare.", "error");
        } finally {
            setUploading(false);
            refetch();
        }
    };

    const navigation = current && (
        <Box my={1} display="flex" justifyContent="space-between" alignItems="center">
            <div>
            <Button disabled={index === 0} onClick={() => setSelectedId(files[index - 1].file.id)}>← Precedente</Button>
            <Button disabled={index === files.length - 1} onClick={() => setSelectedId(files[index + 1].file.id)}>Successiva
                →</Button>
            </div>
            <Typography
                variant="body2">Bolla {index + 1} di {files.length} · {files.filter(item => item.file.status === "CO").length} pronte</Typography>
            <Button color="primary" variant="contained" startIcon={<CloudUploadIcon/>}
                    onClick={() => setUploadOpen(true)}>Carica bolle</Button>
        </Box>
    );

    return (
        <Box py={2}>
            <Title title="Converti bolle"/>
            {loading ? <Box p={6} textAlign="center"><CircularProgress/></Box> : current ? <>
                {navigation}
                <ConvertNoteFilesWorkspace key={current.note.id} convertNote={{...current.note, files: [current.file]}}
                                           showUpload={false} split hideNavigation onRefresh={refetch}/>
            </> : <Paper variant="outlined">
                <Box p={6} textAlign="center">
                    <CloudUploadIcon color="primary" style={{fontSize: 48}}/>
                    <Typography
                        variant="h6">{loadError ? "Impossibile caricare le bolle" : "Nessuna bolla da convertire"}</Typography>
                    <Typography
                        color="textSecondary">{loadError ? "Riprova ad aggiornare i dati." : "Carica PDF o immagini. Cliente e destinatario saranno riconosciuti automaticamente."}</Typography>
                    <Box mt={2}><Button color="primary"
                                        onClick={loadError ? refetch : () => setUploadOpen(true)}>{loadError ? "Riprova" : "Carica le prime bolle"}</Button></Box>
                </Box>
            </Paper>}
            <Dialog open={uploadOpen} onClose={closeUpload} fullWidth maxWidth="sm">
                <DialogTitle>Carica bolle</DialogTitle>
                <DialogContent dividers>
                    <Typography color="textSecondary">Non servono altre informazioni: carica i documenti e verifica i
                        dati
                        dopo l’elaborazione.</Typography>
                    <ConvertNoteDraftFiles files={draftFiles} onAdd={addFiles}
                                           onRemove={index => setDraftFiles(files => files.filter((_, i) => i !== index))}
                                           disabled={uploading || uploaded.current} autoStart={autoStart}
                                           onAutoStartChange={setAutoStart}/>
                </DialogContent>
                <DialogActions>
                    <Button disabled={uploading} onClick={closeUpload}>Chiudi</Button>
                    <Button variant="contained" color="primary" disabled={uploading || !draftFiles.length}
                            onClick={upload}>
                        {uploading ? "Caricamento…" : uploaded.current ? "Riprova avvio" : autoStart ? "Carica e avvia" : "Carica"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>);
}
